/**
 * @fileoverview Factory for the local-first report/document template service, shared by
 * tenant-web and system-web (closes G-DRY-SVC — two ~95% identical services).
 * @module local-db/services/createReportTemplateService
 *
 * Responsibilities: - Provide one implementation of report-template CRUD, publish/archive,
 *                     duplicate, and advanced-linked lookups against the Dexie
 *                     `report_templates` table, parameterized by the DB getter + source level.
 * Dependencies:     - ../reporting/reportTemplateTypes (ReportTemplateLocal)
 *                   - ../replication/replicationFactory (wakeLocalWrite)
 * Architecture:     - Local-first: Dexie read/write first, then wakeLocalWrite() to trigger
 *                     background sync. No API calls. tenant-web injects getTenantDb; system-web
 *                     injects getSystemDb.
 *                   - Audit logging is injected via optional writeAuditLog callback to avoid
 *                     coupling this package to @firesquirrel/audit.
 * Notes:            - sourceLevel is recorded in `derivedFrom` on duplicate.
 */

import type { ReportTemplateLocal } from '../reporting/reportTemplateTypes';
import { validateReportTemplateDataScopeForPublish } from '@firesquirrel/import-core/complex-inspections';
import { wakeLocalWrite } from '../replication/replicationFactory';

/** Minimal Dexie table surface the service needs. */
interface ReportTemplateTable {
  toArray(): Promise<unknown[]>;
  get(id: string): Promise<unknown>;
  put(rec: Record<string, any>): Promise<unknown>;
  patch(id: string, patch: Record<string, any>): Promise<unknown>;
}

/**
 * @contract ReportTemplateServiceDeps
 * Purpose: Injected dependencies that differentiate the tenant vs system service.
 * Fields:  getDb (Dexie getter exposing report_templates), sourceLevel (for derivedFrom).
 * Rules:   getDb must return a DB with a `report_templates` table.
 * Sync:    N/A.
 */
/**
 * @contract WriteAuditLogFn
 * Purpose: Injected audit writer so the factory can log mutations without depending on @firesquirrel/audit.
 * Fields:  opts (collection, documentId, action, summary, optional changes)
 * Rules:   Must not throw — audit failure must never block a mutation.
 * Sync:    local-first (writes to Dexie audit_logs, synced in background).
 */
export type WriteAuditLogFn = (opts: {
  collection: string;
  documentId: string;
  action: string;
  summary: string;
  changes?: Array<{ field: string; oldValue?: unknown; newValue?: unknown; fieldType?: string }>;
}) => Promise<void>;

export interface ReportTemplateServiceDeps {
  getDb: () => Promise<{ report_templates: ReportTemplateTable }>;
  sourceLevel: 'department' | 'organization' | 'system';
  /** _id prefix for new/duplicated records. Default 'rtpl' (system-web uses 'sys-rtpl'). */
  idPrefix?: string;
  /** Suffix appended to a duplicated template's name. Default ' (copy)'. */
  copyNameSuffix?: string;
  /** Actor recorded for publish/duplicate when no explicit actor is passed (e.g. 'system-admin'). */
  defaultActor?: string;
  /** Optional audit logger. When provided, all mutations write to Dexie audit_logs. */
  writeAuditLog?: WriteAuditLogFn;
}

export interface ListReportTemplatesParams {
  documentType?: ReportTemplateLocal['documentType'];
  status?: ReportTemplateLocal['status'];
  includeDeleted?: boolean;
}

/**
 * @contract ReportTemplateService
 * Purpose: The local-first report/document template API returned by the factory.
 * Fields:  list/get/create/update/delete/publish/archive/duplicate + advanced-linked lookups.
 * Rules:   All reads from Dexie; all writes Dexie-first then wakeLocalWrite.
 * Sync:    local-first; backend MongoDB reportTemplates is reconciled by replication.
 */
export interface ReportTemplateService {
  listReportTemplates(params?: ListReportTemplatesParams): Promise<ReportTemplateLocal[]>;
  getReportTemplate(id: string): Promise<ReportTemplateLocal | null>;
  createReportTemplate(
    data: Omit<ReportTemplateLocal, '_id' | 'createdAt' | 'updatedAt' | 'version'>,
  ): Promise<ReportTemplateLocal>;
  updateReportTemplate(id: string, patch: Partial<ReportTemplateLocal>): Promise<ReportTemplateLocal | null>;
  deleteReportTemplate(id: string): Promise<boolean>;
  publishReportTemplate(id: string, publishedBy?: string): Promise<ReportTemplateLocal | null>;
  archiveReportTemplate(id: string): Promise<ReportTemplateLocal | null>;
  duplicateReportTemplate(id: string, createdBy?: string): Promise<ReportTemplateLocal | null>;
  listAdvancedTemplatesForInspectionTemplate(inspectionTemplateId: string): Promise<ReportTemplateLocal[]>;
  listAdvancedLinkedTemplates(): Promise<ReportTemplateLocal[]>;
  validateAdvancedTemplateLink(
    reportTemplateId: string,
    inspectionTemplateId: string,
  ): Promise<{ valid: boolean; reason?: string }>;
}

function nowIso(): string {
  return new Date().toISOString();
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * @process createReportTemplateService
 * Steps:    Build a report-template service bound to the given Dexie getter + source level.
 * Inputs:   ReportTemplateServiceDeps.
 * Outputs:  ReportTemplateService.
 * Failure Handling: get/update return null when the record is absent; delete returns false.
 * Idempotency: NOT REQUIRED (CRUD with version bump).
 */
export function createReportTemplateService(deps: ReportTemplateServiceDeps): ReportTemplateService {
  const { getDb, sourceLevel } = deps;
  const idPrefix = deps.idPrefix ?? 'rtpl';
  const copyNameSuffix = deps.copyNameSuffix ?? ' (copy)';
  const defaultActor = deps.defaultActor;
  const auditLog = deps.writeAuditLog;

  /** Fields excluded from audit diffs (sync metadata, timestamps, internal). */
  const SKIP = new Set([
    '_id', '_rev', '_replicator', '_syncSeq',
    'createdAt', 'updatedAt', 'createdBy', 'updatedBy',
    'deletedAt', 'deletedBy', 'version',
  ]);

  function diffFields(
    before: Record<string, unknown>,
    after: Record<string, unknown>,
  ): Array<{ field: string; oldValue?: unknown; newValue?: unknown; fieldType?: string }> {
    const changes: Array<{ field: string; oldValue?: unknown; newValue?: unknown; fieldType?: string }> = [];
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const k of keys) {
      if (SKIP.has(k)) continue;
      const o = before[k];
      const n = after[k];
      if (JSON.stringify(o) !== JSON.stringify(n)) {
        const val = n !== undefined ? n : o;
        const fieldType = val === null || val === undefined ? 'null' : Array.isArray(val) ? 'array' : typeof val;
        changes.push({ field: k, oldValue: o ?? null, newValue: n ?? null, fieldType });
      }
    }
    return changes;
  }

  async function safeAudit(opts: Parameters<WriteAuditLogFn>[0]): Promise<void> {
    if (!auditLog) return;
    try { await auditLog(opts); } catch (e) { console.warn('[audit] safeAudit failed:', e); }
  }

  async function listReportTemplates(params: ListReportTemplatesParams = {}): Promise<ReportTemplateLocal[]> {
    const db = await getDb();
    let results = (await db.report_templates.toArray()) as ReportTemplateLocal[];
    if (!params.includeDeleted) results = results.filter((r) => !r._deleted);
    if (params.documentType) results = results.filter((r) => r.documentType === params.documentType);
    if (params.status) results = results.filter((r) => r.status === params.status);
    return results.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async function getReportTemplate(id: string): Promise<ReportTemplateLocal | null> {
    const db = await getDb();
    return ((await db.report_templates.get(id)) as ReportTemplateLocal | undefined) ?? null;
  }

  async function createReportTemplate(
    data: Omit<ReportTemplateLocal, '_id' | 'createdAt' | 'updatedAt' | 'version'>,
  ): Promise<ReportTemplateLocal> {
    const db = await getDb();
    const now = nowIso();
    const record = {
      ...data,
      reportDataScope: data.reportDataScope ?? 'singleCase',
      _id: newId(idPrefix),
      version: 1,
      createdAt: now,
      updatedAt: now,
      _deleted: false,
    } as ReportTemplateLocal;
    await db.report_templates.put(record as unknown as Record<string, any>);
    wakeLocalWrite('report_templates');
    await safeAudit({
      collection: 'reportTemplates',
      documentId: record._id,
      action: 'create',
      summary: `Created report template "${record.name}"`,
    });
    return record;
  }

  async function updateReportTemplate(
    id: string,
    patch: Partial<ReportTemplateLocal>,
  ): Promise<ReportTemplateLocal | null> {
    const db = await getDb();
    const current = (await db.report_templates.get(id)) as ReportTemplateLocal | undefined;
    if (!current) return null;
    const updated = {
      ...current,
      ...patch,
      _id: id,
      version: (current.version ?? 1) + 1,
      updatedAt: nowIso(),
    } as ReportTemplateLocal;
    await db.report_templates.patch(id, updated as unknown as Record<string, any>);
    wakeLocalWrite('report_templates');
    let changes: Array<{ field: string; oldValue?: unknown; newValue?: unknown; fieldType?: string }> | undefined;
    try { changes = diffFields(current as unknown as Record<string, unknown>, updated as unknown as Record<string, unknown>); } catch { /* diff failure must not block mutation */ }
    await safeAudit({
      collection: 'reportTemplates',
      documentId: id,
      action: 'update',
      summary: `Updated report template "${updated.name}"`,
      changes,
    });
    return updated;
  }

  async function deleteReportTemplate(id: string): Promise<boolean> {
    const db = await getDb();
    const existing = await db.report_templates.get(id);
    if (!existing) return false;
    await db.report_templates.patch(id, { _deleted: true, deletedAt: nowIso(), updatedAt: nowIso() });
    wakeLocalWrite('report_templates');
    await safeAudit({
      collection: 'reportTemplates',
      documentId: id,
      action: 'delete',
      summary: `Deleted report template "${(existing as ReportTemplateLocal).name}"`,
      changes: [{ field: '_deleted', oldValue: (existing as Record<string, unknown>)._deleted ?? false, newValue: true, fieldType: 'boolean' }],
    });
    return true;
  }

  async function publishReportTemplate(id: string, publishedBy?: string): Promise<ReportTemplateLocal | null> {
    const current = await getReportTemplate(id);
    if (!current) return null;
    const validation = validateReportTemplateDataScopeForPublish({
      ...current,
      status: 'published',
      reportDataScope: current.reportDataScope ?? 'singleCase',
    });
    if (!validation.valid) {
      throw new Error(`Report template cannot be published: ${validation.issues.map((issue) => issue.message).join('; ')}`);
    }
    const by = publishedBy ?? defaultActor;
    const patch: Partial<ReportTemplateLocal> = { status: 'published', publishedAt: nowIso() };
    if (by) patch.publishedBy = by;
    return updateReportTemplate(id, patch);
  }

  function archiveReportTemplate(id: string): Promise<ReportTemplateLocal | null> {
    return updateReportTemplate(id, { status: 'archived' });
  }

  async function listAdvancedTemplatesForInspectionTemplate(
    inspectionTemplateId: string,
  ): Promise<ReportTemplateLocal[]> {
    const all = await listReportTemplates();
    return all.filter(
      (t) =>
        t.advancedBinding?.mode === 'advancedLinked' &&
        t.advancedBinding.linkedInspectionTemplateId === inspectionTemplateId,
    );
  }

  async function listAdvancedLinkedTemplates(): Promise<ReportTemplateLocal[]> {
    const all = await listReportTemplates();
    return all.filter((t) => t.advancedBinding?.mode === 'advancedLinked');
  }

  async function validateAdvancedTemplateLink(
    reportTemplateId: string,
    inspectionTemplateId: string,
  ): Promise<{ valid: boolean; reason?: string }> {
    const template = await getReportTemplate(reportTemplateId);
    if (!template) return { valid: false, reason: 'Document template not found' };
    if (template.advancedBinding?.mode !== 'advancedLinked') {
      return { valid: false, reason: 'Document template is not an advanced linked template' };
    }
    if (template.advancedBinding.linkedInspectionTemplateId !== inspectionTemplateId) {
      return {
        valid: false,
        reason: `Template is linked to a different inspection template (${template.advancedBinding.linkedInspectionTemplateName || template.advancedBinding.linkedInspectionTemplateId})`,
      };
    }
    return { valid: true };
  }

  async function duplicateReportTemplate(id: string, createdBy?: string): Promise<ReportTemplateLocal | null> {
    const db = await getDb();
    const src = (await db.report_templates.get(id)) as ReportTemplateLocal | undefined;
    if (!src) return null;
    const now = nowIso();
    const actor = createdBy ?? defaultActor;
    const copy = {
      ...src,
      _id: newId(idPrefix),
      name: `${src.name}${copyNameSuffix}`,
      status: 'draft',
      version: 1,
      publishedAt: undefined,
      publishedBy: undefined,
      derivedFrom: {
        reportTemplateId: src._id,
        reportTemplateVersion: src.version,
        sourceLevel,
        copiedAt: now,
        copiedBy: actor,
      },
      createdBy: actor,
      createdAt: now,
      updatedBy: actor,
      updatedAt: now,
      _deleted: false,
      deletedAt: undefined,
      deletedBy: undefined,
    } as ReportTemplateLocal;
    await db.report_templates.put(copy as unknown as Record<string, any>);
    wakeLocalWrite('report_templates');
    await safeAudit({
      collection: 'reportTemplates',
      documentId: copy._id,
      action: 'create',
      summary: `Duplicated report template "${src.name}" as "${copy.name}"`,
    });
    return copy;
  }

  return {
    listReportTemplates,
    getReportTemplate,
    createReportTemplate,
    updateReportTemplate,
    deleteReportTemplate,
    publishReportTemplate,
    archiveReportTemplate,
    duplicateReportTemplate,
    listAdvancedTemplatesForInspectionTemplate,
    listAdvancedLinkedTemplates,
    validateAdvancedTemplateLink,
  };
}
